import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Users, Mail, Copy, UserPlus, Crown, Shield, User, X, Loader2, CheckCircle, Trash2, Globe, Plus, RefreshCw, Star, AlertCircle, ClipboardCopy } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizationSettings() {
    const { getAuthHeader, user } = useAuth();
    const [organization, setOrganization] = useState(null);
    const [members, setMembers] = useState([]);
    const [stats, setStats] = useState(null);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviting, setInviting] = useState(false);

    // Custom Domains state
    const [domains, setDomains] = useState([]);
    const [newDomain, setNewDomain] = useState('');
    const [addingDomain, setAddingDomain] = useState(false);
    const [verifyingId, setVerifyingId] = useState(null);
    const [expandedDomainId, setExpandedDomainId] = useState(null);

    useEffect(() => {
        loadOrganizationData();
    }, []);

    const loadOrganizationData = async () => {
        setLoading(true);
        try {
            const [orgRes, membersRes, statsRes, invitesRes, domainsRes] = await Promise.all([
                axios.get(`${API}/organizations/current`, { headers: getAuthHeader() }),
                axios.get(`${API}/organizations/members`, { headers: getAuthHeader() }),
                axios.get(`${API}/organizations/stats`, { headers: getAuthHeader() }),
                axios.get(`${API}/organizations/invites`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
                axios.get(`${API}/organizations/domains`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
            ]);
            setOrganization(orgRes.data);
            setMembers(membersRes.data);
            setStats(statsRes.data);
            setInvites(invitesRes.data);
            setDomains(domainsRes.data);
        } catch (err) {
            console.error('Failed to load org data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail) return;
        
        setInviting(true);
        try {
            const res = await axios.post(`${API}/organizations/invites`, {
                email: inviteEmail,
                role: inviteRole
            }, { headers: getAuthHeader() });
            
            toast.success(`Invite sent to ${inviteEmail}`);
            setInviteEmail('');
            loadOrganizationData();
            
            // Show invite link
            if (res.data.invite_code) {
                toast.info(`Invite code: ${res.data.invite_code}`, { duration: 10000 });
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send invite');
        } finally {
            setInviting(false);
        }
    };

    const cancelInvite = async (inviteId) => {
        try {
            await axios.delete(`${API}/organizations/invites/${inviteId}`, { headers: getAuthHeader() });
            toast.success('Invite cancelled');
            loadOrganizationData();
        } catch {
            toast.error('Failed to cancel invite');
        }
    };

    const updateMemberRole = async (userId, role) => {
        try {
            await axios.put(`${API}/organizations/members/${userId}/role`, { role }, { headers: getAuthHeader() });
            toast.success('Member role updated');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update role');
        }
    };

    const removeMember = async (userId) => {
        if (!window.confirm('Are you sure you want to remove this member?')) return;
        try {
            await axios.delete(`${API}/organizations/members/${userId}`, { headers: getAuthHeader() });
            toast.success('Member removed');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove member');
        }
    };

    // ─── Custom Domain Handlers ─────────────────────────────────────────
    const handleAddDomain = async (e) => {
        e.preventDefault();
        if (!newDomain.trim()) return;
        setAddingDomain(true);
        try {
            const res = await axios.post(`${API}/organizations/domains`, { domain: newDomain.trim() }, { headers: getAuthHeader() });
            toast.success(`Domain ${newDomain} added! Follow the verification instructions below.`);
            setNewDomain('');
            setExpandedDomainId(res.data.domain_id);
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add domain');
        } finally {
            setAddingDomain(false);
        }
    };

    const handleVerifyDomain = async (domainId) => {
        setVerifyingId(domainId);
        try {
            const res = await axios.post(`${API}/organizations/domains/${domainId}/verify`, {}, { headers: getAuthHeader() });
            if (res.data.verified) {
                toast.success('Domain verified successfully!');
            } else {
                toast.warning(res.data.message || 'Verification failed. Check your DNS records.');
            }
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Verification failed');
        } finally {
            setVerifyingId(null);
        }
    };

    const handleDeleteDomain = async (domainId, domainName) => {
        if (!window.confirm(`Remove domain "${domainName}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/organizations/domains/${domainId}`, { headers: getAuthHeader() });
            toast.success('Domain removed');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to remove domain');
        }
    };

    const handleSetPrimary = async (domainId) => {
        try {
            await axios.post(`${API}/organizations/domains/${domainId}/set-primary`, {}, { headers: getAuthHeader() });
            toast.success('Primary domain updated');
            loadOrganizationData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set primary domain');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getDomainStatusBadge = (status) => {
        const styles = {
            pending: 'bg-amber-50 text-amber-700 border-amber-200',
            verifying: 'bg-blue-50 text-blue-700 border-blue-200',
            verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            failed: 'bg-red-50 text-red-700 border-red-200',
        };
        const labels = {
            pending: 'Pending Verification',
            verifying: 'Verifying...',
            verified: 'Verified',
            active: 'Active',
            failed: 'Failed',
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
                {status === 'active' || status === 'verified' ? <CheckCircle className="w-3 h-3" /> : status === 'pending' ? <AlertCircle className="w-3 h-3" /> : null}
                {labels[status] || status}
            </span>
        );
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'owner': return <Crown className="w-3.5 h-3.5 text-amber-500" />;
            case 'admin': return <Shield className="w-3.5 h-3.5 text-stone-600" />;
            default: return <User className="w-3.5 h-3.5 text-stone-400" />;
        }
    };

    const canManageMembers = user?.org_role === 'owner' || user?.org_role === 'admin';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-stone-700 animate-spin" />
            </div>
        );
    }

    if (!organization || !organization.org_id) {
        return (
            <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-stone-900">No Organization</h3>
                <p className="text-sm text-stone-500 mt-1">You're not part of any organization yet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Organization Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-stone-1000 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                        {organization.name?.charAt(0) || 'O'}
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-stone-900">{organization.name}</h2>
                        <p className="text-sm text-stone-500">{organization.domain || 'No domain set'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        organization.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                        organization.plan === 'professional' ? 'bg-stone-100 text-stone-900' :
                        organization.plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                        'bg-stone-100 text-stone-600'
                    }`}>
                        {organization.plan?.charAt(0).toUpperCase() + organization.plan?.slice(1)} Plan
                    </span>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl border border-stone-200 bg-white">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-stone-500">Users</span>
                            <Users className="w-4 h-4 text-stone-400" />
                        </div>
                        <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.users}</p>
                        <p className="text-xs text-stone-400">of {stats.limits.max_users}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-stone-200 bg-white">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-stone-500">Contacts</span>
                            <Users className="w-4 h-4 text-stone-400" />
                        </div>
                        <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.contacts}</p>
                        <p className="text-xs text-stone-400">of {stats.limits.max_contacts}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-stone-200 bg-white">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-stone-500">Companies</span>
                            <Building2 className="w-4 h-4 text-stone-400" />
                        </div>
                        <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.accounts}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-stone-200 bg-white">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-stone-500">Deals</span>
                            <Building2 className="w-4 h-4 text-stone-400" />
                        </div>
                        <p className="text-2xl font-semibold text-stone-900 mt-1">{stats.stats.deals}</p>
                    </div>
                </div>
            )}

            {/* Members */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                    <h3 className="font-semibold text-stone-900">Team Members</h3>
                    <span className="text-xs text-stone-500">{members.length} members</span>
                </div>
                <div className="divide-y divide-stone-100">
                    {members.map(member => (
                        <div key={member.user_id} className="px-5 py-3 flex items-center justify-between hover:bg-stone-50">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center">
                                    {member.picture ? (
                                        <img src={member.picture} alt="" className="w-full h-full rounded-full" />
                                    ) : (
                                        <span className="text-sm font-medium text-stone-700">{member.name?.charAt(0)}</span>
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-stone-900">{member.name}</span>
                                        {getRoleIcon(member.org_role)}
                                        {member.user_id === user?.user_id && (
                                            <span className="text-xs bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">You</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-stone-500">{member.email}</p>
                                </div>
                            </div>
                            {canManageMembers && member.user_id !== user?.user_id && member.org_role !== 'owner' && (
                                <div className="flex items-center gap-2">
                                    <select
                                        value={member.org_role}
                                        onChange={(e) => updateMemberRole(member.user_id, e.target.value)}
                                        className="text-xs border border-stone-200 rounded px-2 py-1"
                                    >
                                        <option value="member">Member</option>
                                        <option value="admin">Admin</option>
                                        {user?.org_role === 'owner' && <option value="owner">Owner</option>}
                                    </select>
                                    <button
                                        onClick={() => removeMember(member.user_id)}
                                        className="p-1 text-stone-400 hover:text-red-500"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Invite Section */}
            {canManageMembers && (
                <div className="bg-white rounded-xl border border-stone-200 p-5">
                    <h3 className="font-semibold text-stone-900 mb-4">Invite Team Member</h3>
                    <form onSubmit={handleInvite} className="flex items-end gap-3">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-stone-600 mb-1">Email Address</label>
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="colleague@company.com"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                data-testid="invite-email-input"
                            />
                        </div>
                        <div className="w-32">
                            <label className="block text-xs font-medium text-stone-600 mb-1">Role</label>
                            <select
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                            >
                                <option value="member">Member</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <Button type="submit" disabled={inviting || !inviteEmail} className="h-10" data-testid="send-invite-btn">
                            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
                            Send Invite
                        </Button>
                    </form>

                    {/* Pending Invites */}
                    {invites.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <h4 className="text-xs font-medium text-stone-500 mb-2">Pending Invites</h4>
                            <div className="space-y-2">
                                {invites.map(invite => (
                                    <div key={invite.invite_id} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-amber-600" />
                                            <span className="text-sm text-stone-700">{invite.email}</span>
                                            <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">{invite.role}</span>
                                        </div>
                                        <button onClick={() => cancelInvite(invite.invite_id)} className="text-stone-400 hover:text-red-500">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Custom Domains */}
            {canManageMembers && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-stone-700" />
                            <h3 className="font-semibold text-stone-900">Custom Domains</h3>
                        </div>
                        <span className="text-xs text-stone-500">{domains.length} domain{domains.length !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="p-5 space-y-4">
                        {/* Add Domain Form */}
                        <form onSubmit={handleAddDomain} className="flex items-end gap-3" data-testid="add-domain-form">
                            <div className="flex-1">
                                <label className="block text-xs font-medium text-stone-600 mb-1">Domain Name</label>
                                <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    placeholder="sales.yourcompany.com"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                    data-testid="add-domain-input"
                                />
                            </div>
                            <Button type="submit" disabled={addingDomain || !newDomain.trim()} className="h-10" data-testid="add-domain-btn">
                                {addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                                Add Domain
                            </Button>
                        </form>

                        {/* Domains List */}
                        {domains.length === 0 ? (
                            <div className="text-center py-8" data-testid="no-domains-placeholder">
                                <Globe className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                                <p className="text-sm text-stone-500">No custom domains added yet</p>
                                <p className="text-xs text-stone-400 mt-1">Add a domain above to get started with branded links and emails</p>
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="domains-list">
                                {domains.map(domain => (
                                    <div key={domain.domain_id} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`domain-card-${domain.domain_id}`}>
                                        {/* Domain Header Row */}
                                        <div className="px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${domain.status === 'active' ? 'bg-emerald-100' : domain.status === 'verified' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                                                    <Globe className={`w-4 h-4 ${domain.status === 'active' ? 'text-emerald-600' : domain.status === 'verified' ? 'text-emerald-500' : 'text-amber-500'}`} />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-stone-900 truncate" data-testid={`domain-name-${domain.domain_id}`}>{domain.domain}</span>
                                                        {domain.is_primary && (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-stone-100 text-stone-900 rounded" data-testid={`domain-primary-badge-${domain.domain_id}`}>
                                                                <Star className="w-3 h-3" /> Primary
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        {getDomainStatusBadge(domain.status)}
                                                        {domain.ssl_status === 'active' && (
                                                            <span className="text-xs text-emerald-600 flex items-center gap-0.5">
                                                                <Shield className="w-3 h-3" /> SSL
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                {domain.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 text-xs"
                                                            onClick={() => setExpandedDomainId(expandedDomainId === domain.domain_id ? null : domain.domain_id)}
                                                            data-testid={`domain-instructions-toggle-${domain.domain_id}`}
                                                        >
                                                            {expandedDomainId === domain.domain_id ? 'Hide' : 'Setup'}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            className="h-8 text-xs"
                                                            onClick={() => handleVerifyDomain(domain.domain_id)}
                                                            disabled={verifyingId === domain.domain_id}
                                                            data-testid={`domain-verify-btn-${domain.domain_id}`}
                                                        >
                                                            {verifyingId === domain.domain_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                                                            Verify
                                                        </Button>
                                                    </>
                                                )}
                                                {domain.status === 'active' && !domain.is_primary && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 text-xs"
                                                        onClick={() => handleSetPrimary(domain.domain_id)}
                                                        data-testid={`domain-set-primary-btn-${domain.domain_id}`}
                                                    >
                                                        <Star className="w-3.5 h-3.5 mr-1" /> Set Primary
                                                    </Button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteDomain(domain.domain_id, domain.domain)}
                                                    className="p-1.5 text-stone-400 hover:text-red-500 transition-colors"
                                                    data-testid={`domain-delete-btn-${domain.domain_id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded Verification Instructions */}
                                        {expandedDomainId === domain.domain_id && domain.status === 'pending' && (
                                            <div className="px-4 py-3 bg-stone-50 border-t border-stone-200" data-testid={`domain-instructions-${domain.domain_id}`}>
                                                <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">DNS Verification Instructions</h4>
                                                <p className="text-xs text-stone-500 mb-3">Add a TXT record to your DNS provider to verify ownership of this domain.</p>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                        <div>
                                                            <span className="text-xs text-stone-500 block">Record Type</span>
                                                            <span className="text-sm font-mono font-medium text-stone-900">TXT</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                        <div className="min-w-0 flex-1 mr-2">
                                                            <span className="text-xs text-stone-500 block">Host / Name</span>
                                                            <span className="text-sm font-mono font-medium text-stone-900 break-all" data-testid={`domain-record-name-${domain.domain_id}`}>_nurturecrm-verification</span>
                                                        </div>
                                                        <button onClick={() => copyToClipboard('_nurturecrm-verification')} className="p-1.5 text-stone-400 hover:text-stone-700 shrink-0" data-testid={`copy-record-name-${domain.domain_id}`}>
                                                            <ClipboardCopy className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <div className="flex items-center justify-between p-2.5 bg-white border border-stone-200 rounded-lg">
                                                        <div className="min-w-0 flex-1 mr-2">
                                                            <span className="text-xs text-stone-500 block">Value</span>
                                                            <span className="text-sm font-mono font-medium text-stone-900 break-all" data-testid={`domain-record-value-${domain.domain_id}`}>{domain.verification_token}</span>
                                                        </div>
                                                        <button onClick={() => copyToClipboard(domain.verification_token)} className="p-1.5 text-stone-400 hover:text-stone-700 shrink-0" data-testid={`copy-record-value-${domain.domain_id}`}>
                                                            <ClipboardCopy className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                {domain.error_message && (
                                                    <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid={`domain-error-${domain.domain_id}`}>
                                                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                        <p className="text-xs text-red-700">{domain.error_message}</p>
                                                    </div>
                                                )}
                                                <p className="text-xs text-stone-400 mt-3">DNS changes typically take 5-30 minutes to propagate. Click "Verify" once your record is set.</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
